import { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'

type Props = { children: ReactNode }
type State = { hasError: boolean; eventId?: string }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Send error to Sentry with component stack trace
    try {
      const eventId = Sentry.captureException(error, {
        contexts: {
          react: { componentStack: errorInfo.componentStack },
        },
      })
      this.setState({ eventId })
    } catch (sentryError) {
      // Sentry not available (dev mode or not configured)
      console.error('Sentry capture failed:', sentryError)
    }

    // Also log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="container mx-auto px-4 pt-28">
          <div className="rounded-lg border bg-red-50 text-red-800 p-6 max-w-2xl mx-auto">
            <div className="font-semibold mb-2 text-lg">Something went wrong</div>
            <div className="text-sm mb-4">
              An error report was sent automatically. Please refresh the page to try again.
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
              >
                Refresh Page
              </button>
              {this.state.eventId && (
                <button
                  className="px-4 py-2 border border-red-300 rounded text-sm hover:bg-red-100"
                  onClick={() => {
                    // Guard against eventId becoming undefined between render and click
                    if (this.state.eventId) {
                      Sentry.showReportDialog({ eventId: this.state.eventId })
                    }
                  }}
                >
                  Report Details
                </button>
              )}
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}


